import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button, Select, Input } from '../atoms';
import { DataTable, StatCard, StatCardRow } from '../organisms';
import {
  useActivityStream,
  mergeActivityEvents,
  ActivityStreamEvent,
  ActivityStreamStatus,
} from '../lib/useActivityStream';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * ActivityLog — /activity-log
 *
 * The manager-facing org-wide audit feed: humanized audit_logs across the whole
 * org, filterable by entity type, user, and date. Reads the existing pull
 * endpoint GET /api/activity with server-side filters. Rows deep-link to the
 * affected record where the entity type maps to a routed detail page.
 *
 * Domain 02.1 — Data Integrity & Audit. Gated to manager/analyst roles; agents
 * and client-role users never reach it.
 */

interface CurrentUser {
  role: string;
}

interface ActivityUser {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface ActivityRow {
  id: string;
  timestamp: string;
  actor: string;
  entity_type: string;
  entity_id: string;
  entity_label: string;
  action: string;
  field: string | null;
  message: string;
}

interface ActivityResponse {
  events: ActivityRow[];
  limit: number;
  offset: number;
}

const MANAGER_ROLES = ['super_admin', 'admin', 'administrator', 'analyst'];

// Entity types that appear in the audit trail, with a friendly label. Kept as a
// fixed list (audit_logs.entity_type is the plural table name) so the filter is
// predictable rather than derived from whatever happens to be in the feed.
const ENTITY_TYPES: { value: string; label: string }[] = [
  { value: 'clients', label: 'Businesses' },
  { value: 'parent_clients', label: 'Parent organizations' },
  { value: 'contacts', label: 'People' },
  { value: 'assessments', label: 'Assessments' },
  { value: 'referrals', label: 'Referrals' },
  { value: 'partners', label: 'Partners' },
  { value: 'capabilities', label: 'Capabilities' },
  { value: 'programs', label: 'Programmes' },
  { value: 'enrollments', label: 'Enrollments' },
  { value: 'program_sessions', label: 'Sessions' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'documents', label: 'Documents' },
  { value: 'information_requests', label: 'Information requests' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'invoice_items', label: 'Invoice items' },
  { value: 'auth', label: 'Authentication' },
];

// entity_type (plural table name) -> the routed list path for its detail page.
// Types without a routed detail (e.g. auth) are intentionally absent.
const ENTITY_ROUTE: Record<string, string> = {
  clients: '/clients',
  parent_clients: '/parent-clients',
  contacts: '/contacts',
  assessments: '/assessments',
  referrals: '/referrals',
  partners: '/partners',
  capabilities: '/capabilities',
  programs: '/programs',
  enrollments: '/enrollments',
  program_sessions: '/program-sessions',
  attendance: '/attendance',
  documents: '/documents',
  information_requests: '/information-requests',
  invoices: '/invoices',
  invoice_items: '/invoice-items',
  users: '/user-management',
};

const actionVariant: Record<string, 'success' | 'info' | 'danger' | 'neutral'> = {
  create: 'success',
  update: 'info',
  delete: 'danger',
};

function userLabel(u: ActivityUser): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.email || u.id;
}

export function ActivityLog() {
  const navigate = useNavigate();
  const [entityType, setEntityType] = useState('');
  const [userId, setUserId] = useState('');
  const [since, setSince] = useState('');

  const userQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
  });

  const usersQuery = useQuery<ActivityUser[]>({
    queryKey: ['users-for-activity'],
    // /api/users-list JOINs contacts, so first/last NAME is available. The plain
    // /api/users (users table) has no name columns → the dropdown fell back to
    // emails AND the person filter (which matches the actor's NAME on each event)
    // never matched. Sourcing names here fixes both the labels and the filter.
    queryFn: ({ signal }) => api.get<ActivityUser[]>('/api/users-list', signal),
  });

  const activityQuery = useQuery<ActivityResponse>({
    queryKey: ['activity-log', entityType, userId, since],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: '100' });
      if (entityType) params.set('entity_type', entityType);
      if (userId) params.set('user_id', userId);
      if (since) params.set('since', since);
      return api.get<ActivityResponse>(`/api/activity?${params.toString()}`, signal);
    },
    enabled: MANAGER_ROLES.includes(userQuery.data?.role ?? ''),
  });

  const isManager = MANAGER_ROLES.includes(userQuery.data?.role ?? '');

  // The org-wide SSE stream is unfiltered; to keep a FILTERED view consistent we
  // apply the same predicate client-side as each live event arrives. entity_type
  // and `since` live on the event; the person filter matches on the resolved
  // actor label (audit_logs carries the actor name, not the user id).
  const selectedActorLabel = useMemo(() => {
    if (!userId) return '';
    const u = (usersQuery.data ?? []).find(x => x.id === userId);
    return u ? userLabel(u) : '';
  }, [userId, usersQuery.data]);

  // Live events pushed since mount, newest-first. Reset when the filter changes
  // so events captured under a prior filter don't bleed into the new view.
  const [liveEvents, setLiveEvents] = useState<ActivityRow[]>([]);
  useEffect(() => { setLiveEvents([]); }, [entityType, userId, since]);

  const handleStreamEvent = useCallback((ev: ActivityStreamEvent) => {
    if (entityType && ev.entity_type !== entityType) return;
    if (since && new Date(ev.timestamp) < new Date(since)) return;
    if (selectedActorLabel && ev.actor !== selectedActorLabel) return;
    setLiveEvents(prev => (prev.some(e => e.id === ev.id) ? prev : [ev, ...prev]));
  }, [entityType, since, selectedActorLabel]);

  const { status: streamStatus } = useActivityStream({
    enabled: isManager,
    onEvent: handleStreamEvent,
  });

  if (userQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }

  if (userQuery.data && !MANAGER_ROLES.includes(userQuery.data.role)) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>The activity log is restricted to manager and analyst roles.</p>
      </div>
    );
  }

  const snapshot = activityQuery.data?.events ?? [];
  // Live-first, then the snapshot; dedupe by id so a reconnect replay that
  // re-delivers an already-shown row collapses to one.
  const events = mergeActivityEvents(liveEvents, snapshot);
  const distinctActors = new Set(events.map(e => e.actor)).size;
  const hasFilters = !!(entityType || userId || since);

  const userOptions = [
    { value: '', label: 'All users' },
    ...(usersQuery.data ?? []).map(u => ({ value: u.id, label: userLabel(u) })),
  ];

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-3xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Activity log</h1>
          <LiveIndicator status={streamStatus} />
        </div>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Every change across the org, newest first. New activity streams in live. Filter by record type, person, or date.
        </p>
      </header>

      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <StatCardRow>
          <StatCard label="Events shown" value={String(events.length)} caption={events.length === 100 ? 'showing latest 100' : 'in current view'} accent="hunter" />
          <StatCard label="People" value={String(distinctActors)} caption="distinct actors in view" accent="info" />
          <StatCard label="Scope" value={hasFilters ? 'Filtered' : 'All activity'} caption={since ? `since ${since}` : 'no date limit'} accent="amber" />
        </StatCardRow>
      </div>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--sp-3)',
        alignItems: 'flex-end',
        marginBottom: 'var(--sp-5)',
      }}>
        <FilterField label="Record type">
          <Select
            options={[{ value: '', label: 'All types' }, ...ENTITY_TYPES]}
            value={entityType}
            onChange={e => setEntityType(e.target.value)}
          />
        </FilterField>
        <FilterField label="Person">
          <Select
            options={userOptions}
            value={userId}
            onChange={e => setUserId(e.target.value)}
          />
        </FilterField>
        <FilterField label="Since">
          <Input type="date" value={since} onChange={e => setSince(e.target.value)} />
        </FilterField>
        {hasFilters && (
          <Button variant="secondary" size="sm" onClick={() => { setEntityType(''); setUserId(''); setSince(''); }}>
            Clear filters
          </Button>
        )}
      </div>

      <ActivityTable
        events={events}
        loading={activityQuery.isLoading}
        error={activityQuery.error as Error | null}
        onRowClick={(row) => {
          const base = ENTITY_ROUTE[row.entity_type];
          if (base) navigate(`${base}/${row.entity_id}`);
        }}
      />
    </>
  );
}

function ActivityTable({
  events, loading, error, onRowClick,
}: {
  events: ActivityRow[];
  loading: boolean;
  error: Error | null;
  onRowClick: (row: ActivityRow) => void;
}) {
  const columns = useMemo<ColumnDef<ActivityRow>[]>(() => [
    {
      accessorKey: 'timestamp',
      header: 'When',
      cell: ({ getValue }) => (
        <span style={{ whiteSpace: 'nowrap', color: 'var(--color-ink-3)' }}>
          {formatDateTime(getValue() as string)}
        </span>
      ),
    },
    { accessorKey: 'actor', header: 'Who' },
    { accessorKey: 'message', header: 'Activity' },
    {
      accessorKey: 'entity_type',
      header: 'Type',
      cell: ({ getValue }) => <Badge variant="neutral">{((getValue() as string | null) ?? '').replace(/_/g, ' ') || '—'}</Badge>,
    },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ getValue }) => {
        const a = getValue() as string;
        return <Badge variant={actionVariant[a] ?? 'neutral'}>{a.replace(/_/g, ' ')}</Badge>;
      },
    },
  ], []);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading activity…</p>;
  if (error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load activity. {error.message}
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>No activity matches these filters.</div>
    );
  }
  return <DataTable data={events} columns={columns} onRowClick={onRowClick} />;
}

/**
 * LiveIndicator — a small dot + label reflecting the SSE connection so the user
 * knows whether the feed is streaming live or has fallen back to the loaded
 * snapshot. `unsupported`/`error` degrade quietly to "snapshot".
 */
function LiveIndicator({ status }: { status: ActivityStreamStatus }) {
  const config: Record<ActivityStreamStatus, { color: string; label: string; pulse: boolean }> = {
    open: { color: 'var(--color-status-green)', label: 'Live', pulse: true },
    connecting: { color: 'var(--color-status-amber)', label: 'Connecting…', pulse: false },
    error: { color: 'var(--color-status-gray)', label: 'Snapshot (offline)', pulse: false },
    unsupported: { color: 'var(--color-status-gray)', label: 'Snapshot', pulse: false },
  };
  const c = config[status];
  return (
    <span
      title={status === 'open' ? 'Streaming live updates' : 'Live updates unavailable — showing the loaded snapshot'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-1)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
        padding: 'var(--sp-0-5) var(--sp-2)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-full)',
        background: 'var(--color-card)',
      }}
    >
      <span style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: c.color,
        boxShadow: c.pulse ? `0 0 0 3px color-mix(in srgb, ${c.color} 25%, transparent)` : 'none',
        flexShrink: 0,
      }} />
      {c.label}
    </span>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 180 }}>
      <span style={{
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</span>
      {children}
    </label>
  );
}
